"use client";

import { useEffect, useMemo, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { useGetJob } from "@/lib/api/generated";
import type { PipelineStage } from "@/lib/api/types";

import { StageDot } from "./stage-dot";

type StageInputProps = {
  jobId: string | null;
  value: string;
  onChange: (stage: PipelineStage) => void;
  /**
   * Exclude the trailing system "Archived" stage from both the trigger's
   * match and the dropdown list. Mirrors the `is_system && position ===
   * max(position)` heuristic previously inlined by the import-profiles
   * modal's native `<select>` — off by default so existing/future callers
   * keep seeing the full stage list unless they opt in.
   */
  excludeArchived?: boolean;
} & Omit<
  React.ComponentProps<typeof Select.Trigger>,
  "value" | "onChange" | "children"
>;

export function StageInput({
  jobId,
  value,
  onChange,
  excludeArchived = false,
  className,
  variant,
  disabled,
  ...triggerProps
}: StageInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const { data: job, isLoading } = useGetJob(jobId ?? "", {
    query: { enabled: !!jobId },
  });

  const stages = useMemo<PipelineStage[]>(() => {
    const sorted = [...(job?.stages ?? [])].sort(
      (a, b) => a.position - b.position,
    );
    if (!excludeArchived || sorted.length === 0) return sorted;
    const maxPos = Math.max(...sorted.map((s) => s.position));
    return sorted.filter((s) => !(s.is_system && s.position === maxPos));
  }, [job?.stages, excludeArchived]);

  const filteredStages = useMemo(() => {
    if (!search.trim()) return stages;
    const q = search.toLowerCase();
    return stages.filter((s) => s.name.toLowerCase().includes(q));
  }, [stages, search]);

  // Derived from the fetched list rather than mirrored into local state, so
  // the trigger stays correct when `value` is set externally (e.g. an
  // initial-stage resolution) without ever going through `onChange`.
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === value) ?? null,
    [stages, value],
  );
  const isDisabled = disabled || !jobId;

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger
        variant={variant}
        className={className}
        active={!!value}
        disabled={isDisabled}
        {...triggerProps}
      >
        <StageDot
          color={selectedStage?.color}
          iconSize={14}
          iconClassName="text-inherit opacity-60"
        />
        <Select.TriggerContent>
          {selectedStage?.name || "Select Stage"}
        </Select.TriggerContent>
        <Select.Icon className="ml-auto" />
      </Select.Trigger>

      <Select.Content
        matchTriggerWidth={false}
        className="w-[380px] min-w-[320px]"
        align="start"
      >
        <Select.Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search stages…"
          autoFocus
        />
        <Select.List>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Icon
                name="IconLoadingCircle"
                size={18}
                className="animate-spin text-contrast/40"
              />
            </div>
          ) : filteredStages.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">
                No stages found
              </p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                {search.trim()
                  ? "No matching stages. Try a different search."
                  : "This job has no stages."}
              </p>
            </div>
          ) : (
            filteredStages.map((stage, i) => (
              <Select.Item
                key={stage.id}
                index={i}
                selected={stage.id === value}
                onSelect={() => {
                  onChange(stage);
                  setOpen(false);
                }}
              >
                <Select.ItemIcon>
                  <StageDot color={stage.color} />
                </Select.ItemIcon>
                <Select.ItemLabel selected={stage.id === value}>
                  {stage.name}
                </Select.ItemLabel>
                {stage.id === value && <Select.ItemCheck />}
              </Select.Item>
            ))
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
